import DeleteIcon from "@mui/icons-material/Delete";
import {
  Checkbox,
  IconButton,
  InputBase,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { Typography } from "@platform/design-system";

import type { Theme } from "@mui/material/styles";
import type {
  ReviewItem,
  ReviewListAction,
  ReviewPanelItem,
} from "./ReviewPanel";

const CommentInput = styled(InputBase)(({ theme }: { theme: Theme }) => ({
  "label + &": {
    marginTop: theme.spacing(1),
  },
  "& .MuiInputBase-input": {
    fontSize: 12,
  },
}));

interface ReviewListItemProps {
  reviewItem: ReviewItem;
  item: ReviewPanelItem;
  itemKey: string;
  isEditing: boolean;
  isChecked: boolean;
  editValue: string;
  onEditValueChange: (value: string) => void;
  onStartEditing: (key: string, initial: string) => void;
  onSaveEditing: (reviewItem: ReviewItem) => void;
  onCancelEditing: () => void;
  onToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUpdate: (action: ReviewListAction) => void;
  onRemoveFromCheckedMap: (id: string) => void;
}

const ReviewListItem: React.FC<ReviewListItemProps> = ({
  reviewItem,
  item,
  itemKey,
  isEditing,
  isChecked,
  editValue,
  onEditValueChange,
  onStartEditing,
  onSaveEditing,
  onCancelEditing,
  onToggle,
  onUpdate,
  onRemoveFromCheckedMap,
}) => {
  const hasComment = Boolean(
    reviewItem.comment && String(reviewItem.comment).trim(),
  );
  const labelId = `checkbox-list-label-${reviewItem.identifier}`;
  const uniqueCellId = `${item.benefitKey}-${reviewItem.cellIdentifier}`;

  return (
    <ListItem
      data-testid={`review-list-item-${uniqueCellId}`}
      secondaryAction={
        <IconButton
          aria-label="delete"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onRemoveFromCheckedMap(uniqueCellId);
            onUpdate({
              type: "remove",
              payload: {
                fieldCommentId: reviewItem.fieldCommentId,
                planId: reviewItem.planId,
              },
            });
          }}
        >
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      }
      onMouseEnter={() =>
        onUpdate({
          type: "highlight",
          payload: {
            benefitKey: item.benefitKey,
            cellIdentifier: reviewItem.cellIdentifier,
          },
        })
      }
      onMouseLeave={() => onUpdate({ type: "clearHighlights" })}
    >
      <ListItemIcon sx={{ minWidth: 0 }}>
        <Checkbox
          edge="start"
          tabIndex={-1}
          slotProps={{ input: { "aria-labelledby": labelId } }}
          checked={isChecked}
          onChange={onToggle}
        />
      </ListItemIcon>

      <ListItemText
        id={labelId}
        sx={{ fontSize: 12 }}
        primary={
          <Typography variant="caption">{reviewItem.identifier}</Typography>
        }
        slotProps={{ secondary: { component: "div" } }}
        secondary={
          isEditing ? (
            <CommentInput
              value={editValue}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onEditValueChange(event.target.value)
              }
              onBlur={() => onSaveEditing(reviewItem)}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                const nativeEvent = event as unknown as KeyboardEvent;
                if (nativeEvent.key === "Enter" && !nativeEvent.shiftKey) {
                  event.preventDefault();
                  onSaveEditing(reviewItem);
                }
                if (nativeEvent.key === "Escape") {
                  event.preventDefault();
                  onCancelEditing();
                }
              }}
              size="small"
              sx={{ fontSize: 12 }}
              fullWidth
              multiline
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              inputProps={{ "aria-label": "edit-comment" }}
            />
          ) : (
            <Typography
              variant="caption"
              color={hasComment ? "textSecondary" : "textDisabled"}
              sx={{
                cursor: "text",
                display: "block",
                width: "100%",
                minHeight: 24,
              }}
              onClick={(event) => {
                event.stopPropagation();
                onStartEditing(itemKey, reviewItem.comment ?? "");
              }}
            >
              {hasComment ? String(reviewItem.comment) : <em>Add comment</em>}
            </Typography>
          )
        }
      />
    </ListItem>
  );
};

export default ReviewListItem;
